import React, { useContext, useEffect, useState } from 'react'
import { Tab as TabComponent, Tabs as TabsComponent } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useHistory, useLocation } from 'react-router-dom'
import { PageHeaderTabsContext } from 'components/page-header/page-header-tabs-section'

const useTabStyles = makeStyles(theme => ({
  tabs: ({ fullWidth, insetLabels, inPageHeader }) => ({
    width: fullWidth ? '100%' : 'auto',
    flex: fullWidth ? undefined : '1 1 auto',
    minWidth: fullWidth ? undefined : 0,
    minHeight: 48,
    borderBottom:
      fullWidth && !inPageHeader
        ? `1px solid ${theme.palette.divider}`
        : 'none',
    '& .MuiTabs-scroller': {
      width: '100%',
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
    ...(inPageHeader
      ? {
        width: '100%',
        margin: 0,
        padding: 0,
        minHeight: 'auto',
      }
      : {}),
    '& .MuiTabs-flexContainer': {
      gap: theme.spacing(1),
      ...(insetLabels
        ? {
          paddingLeft: theme.spacing(4),
          [theme.breakpoints.down('xs')]: {
            paddingLeft: theme.spacing(2),
          },
        }
        : {}),
      ...(inPageHeader
        ? {
          alignItems: 'flex-start',
          marginLeft: 0,
          paddingLeft: 0,
        }
        : {}),
    },
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '1rem',
      lineHeight: 1.5,
      minHeight: 48,
      minWidth: 'auto',
      maxWidth: 'none',
      ...(inPageHeader
        ? {
          display: 'inline-flex',
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          padding: '0 !important',
          minHeight: 'auto',
        }
        : {
          padding: theme.spacing(1.5, 2, 0),
        }),
      color: 'rgba(0, 0, 0, 0.87)',
      opacity: 1,
      '&:hover': {
        color: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'rgba(0, 35, 75, 0.04)',
      },
      '&:active': {
        backgroundColor: 'rgba(0, 83, 255, 0.08)',
      },
      '&.MuiTab-selected': {
        color: '#0053FF',
        fontWeight: 500,
        backgroundColor: 'transparent',
      },
      '&.MuiTab-selected:focus': {
        backgroundColor: 'transparent',
      },
      '&.MuiTab-selected .MuiTab-wrapper': {
        color: '#0053FF',
      },
      '&.MuiTab-selected:hover': {
        color: '#0053FF',
        backgroundColor: 'rgba(0, 35, 75, 0.04)',
      },
      '&.MuiTab-selected:active': {
        backgroundColor: 'rgba(0, 83, 255, 0.08)',
      },
      '& .MuiTab-wrapper': {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: inPageHeader ? 'flex-start' : 'center',
        width: 'fit-content',
        maxWidth: '100%',
        ...(inPageHeader
          ? {
            paddingTop: 0,
            paddingRight: theme.spacing(2),
            paddingBottom: 0,
            paddingLeft: 0,
          }
          : {}),
        '&::after': {
          content: '""',
          display: 'block',
          width: '100%',
          height: 3,
          marginTop: theme.spacing(1),
          borderRadius: 3,
          backgroundColor: 'transparent',
        },
      },
      '&.MuiTab-selected .MuiTab-wrapper::after': {
        backgroundColor: '#0053FF',
      },
    },
  }),
}))

export function tabA11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    'aria-controls': `scrollable-auto-tabpanel-${index}`,
  }
}

export function Tabs({
  children,
  className,
  indicatorColor = 'primary',
  textColor = 'primary',
  fullWidth = true,
  insetLabels = true,
  ...props
}) {
  const inPageHeader = useContext(PageHeaderTabsContext)
  const classes = useTabStyles({
    fullWidth: inPageHeader ? false : fullWidth,
    insetLabels: inPageHeader ? false : insetLabels,
    inPageHeader,
  })

  return (
    <TabsComponent
      className={clsx(classes.tabs, className)}
      indicatorColor={indicatorColor}
      textColor={textColor}
      {...props}
    >
      {React.Children.map(children, (child, index) =>
        React.cloneElement(child, tabA11yProps(index)),
      )}
    </TabsComponent>
  )
}

export const Tab = TabComponent

export function TabPanel({
  children,
  value,
  index,
  shouldBeClickable = true,
  ...other
}) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scrollable-auto-tabpanel-${index}`}
      aria-labelledby={`scrollable-auto-tab-${index}`}
      style={{ pointerEvents: shouldBeClickable ? 'all' : 'none' }}
      {...other}
    >
      {value === index && children}
    </div>
  )
}

const getIncludedTabs = child =>
  !(
    Object.prototype.hasOwnProperty.call(child.props, 'included') &&
    !child.props.included
  )

export function TabPanels({ value, children }) {
  return React.Children.toArray(children)
    .filter(getIncludedTabs)
    .map((child, index) => React.cloneElement(child, { index, value }))
}

export const useTabs = (id, tabPaths) => {
  const location = useLocation()
  const history = useHistory()
  const [tabValue, setTabValue] = useState(0)

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(e => e)
    let newTab = tabPaths.indexOf(pathParts[pathParts.length - 1])
    if (newTab < 0) {
      newTab = 0
    }
    setTabValue(newTab)
  }, [location, tabPaths])

  const setTab = (_, newValue) => {
    if (tabValue === newValue) {
      return
    }
    const pathParts = location.pathname.split('/').filter(e => e)
    if (pathParts[pathParts.length - 1] !== id) {
      pathParts.pop()
    }
    pathParts.push(tabPaths[newValue])
    history.push(`/${pathParts.join('/')}`)
  }

  return {
    tabValue,
    setTab,
  }
}
export const PageHeaderTabs = ({ tabPaths, tabValue, setTab, tabLabels }) => (
  <Tabs
    value={tabValue}
    onChange={setTab}
    variant="scrollable"
    scrollButtons="auto"
    aria-label="location details tabs"
  >
    {tabPaths.map((name, i) => (
      <Tab
        key={name}
        label={tabLabels ? tabLabels[i] : name.replace('-', ' ')}
      />
    ))}
  </Tabs>
)
